import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { MessageCircle, Send, Check, Phone } from "lucide-react";

export default function WhatsAppPanel({ leadId, contact, region, onChange }) {
  const [templates, setTemplates] = useState([]);
  const [tid, setTid] = useState("");
  const [busy, setBusy] = useState(false);
  const [botim, setBotim] = useState("");
  const [thread, setThread] = useState([]);
  const [inbound, setInbound] = useState("");
  const optIn = !!contact?.whatsapp_opt_in;
  const isGulf = region === "GCC" || ["UAE", "Qatar", "Saudi Arabia"].includes(contact?.region);

  const loadThread = () => api.get(`/leads/${leadId}/whatsapp/thread`).then((r) => setThread(r.data)).catch(() => {});
  useEffect(() => { api.get("/whatsapp/templates").then((r) => { setTemplates(r.data); setTid(r.data[0]?.id || ""); }); loadThread(); }, []);

  const selected = templates.find((t) => t.id === tid);
  const blocked = selected?.category === "marketing" && !optIn;

  const send = async () => {
    if (!tid) return;
    setBusy(true);
    try { await api.post(`/leads/${leadId}/whatsapp/send`, { template_id: tid }); toast.success("WhatsApp sent from company number"); loadThread(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  const simulateInbound = async () => {
    if (!inbound.trim()) return;
    try { await api.post(`/leads/${leadId}/whatsapp/inbound`, { body: inbound }); setInbound(""); loadThread(); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleOptIn = async () => {
    try { await api.post(`/contacts/${contact.id}/whatsapp/opt-in`, { opt_in: !optIn }); toast.success(`Opt-in ${!optIn ? "recorded" : "removed"}`); onChange?.(); }
    catch { toast.error("Failed"); }
  };
  const logBotim = async () => {
    if (!botim.trim()) return;
    setBusy(true);
    try { await api.post(`/leads/${leadId}/botim/log`, { note: botim, direction: "outbound" }); toast.success("Botim conversation logged"); setBotim(""); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="whatsapp-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">WhatsApp · Company Number (Cloud API)</div>
        <button onClick={toggleOptIn} data-testid="wa-optin-toggle"
          className="font-mono text-[9px] uppercase px-2 py-1 border" style={{ color: optIn ? "#2F7A3E" : "#B23A2E", borderColor: optIn ? "#2F7A3E" : "#B23A2E" }}>
          {optIn ? "opted-in ✓" : "no opt-in"}
        </button>
      </div>
      <div className="flex gap-2">
        <select value={tid} onChange={(e) => setTid(e.target.value)} data-testid="wa-template"
          className="flex-1 border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]">
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.category}</option>)}
        </select>
        <button onClick={send} disabled={busy || blocked} data-testid="wa-send-btn"
          className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-40">
          <Send className="w-3.5 h-3.5" /> Send
        </button>
      </div>
      {selected && <div className="text-xs text-[#1C2530]/60 mt-2 border-l-2 border-[#DFDAD0] pl-2">{selected.body}</div>}
      {blocked && <div className="font-mono text-[10px] text-[#B23A2E] mt-1">Marketing templates need opt-in first — toggle above once the contact consents.</div>}

      <div className="mt-4 pt-3 border-t border-[#DFDAD0]" data-testid="wa-thread">
        <div className="font-mono text-[10px] uppercase text-[#1C2530]/60 mb-2">Conversation</div>
        {thread.length === 0 ? <div className="font-mono text-[10px] text-[#1C2530]/40 mb-2">NO_MESSAGES_YET</div> : (
          <div className="space-y-1.5 mb-2 max-h-48 overflow-auto">
            {thread.map((m) => (
              <div key={m.id} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-2.5 py-1.5 text-xs ${m.direction === "outbound" ? "bg-[#1C2530] text-[#F5F3EE]" : "bg-[#EFECE5] text-[#1C2530]"}`}>
                  <div className="whitespace-pre-wrap">{m.body}</div>
                  <div className={`font-mono text-[8px] mt-0.5 ${m.direction === "outbound" ? "text-[#F5F3EE]/50" : "text-[#1C2530]/40"}`}>{m.author} · {new Date(m.created_at).toLocaleTimeString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input value={inbound} onChange={(e) => setInbound(e.target.value)} data-testid="wa-inbound-input" placeholder="Simulate an inbound reply from the lead…"
            className="flex-1 border border-[#DFDAD0] px-2 py-1.5 text-xs outline-none focus:border-[#1C2530]" />
          <button onClick={simulateInbound} data-testid="wa-inbound-btn" className="border border-[#DFDAD0] px-3 text-xs font-mono uppercase hover:bg-[#EFECE5]">Receive</button>
        </div>
      </div>

      {isGulf && (
        <div className="mt-4 pt-3 border-t border-[#DFDAD0]" data-testid="botim-section">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase text-[#1C2530]/60 mb-2"><Phone className="w-3.5 h-3.5" /> Botim (Gulf · manual)</div>
          {contact?.botim_handle && <div className="font-mono text-[11px] text-[#1C2530]/60 mb-2">handle: {contact.botim_handle}</div>}
          <div className="flex gap-2">
            <input value={botim} onChange={(e) => setBotim(e.target.value)} data-testid="botim-note" placeholder="Log a Botim call/message note…"
              className="flex-1 border border-[#DFDAD0] px-2 py-1.5 text-sm outline-none focus:border-[#1C2530]" />
            <button onClick={logBotim} disabled={busy} data-testid="botim-log-btn" className="flex items-center gap-1 border border-[#DFDAD0] px-3 text-xs font-mono uppercase hover:bg-[#EFECE5]"><Check className="w-3.5 h-3.5" /> Log</button>
          </div>
        </div>
      )}
    </div>
  );
}
